/**
 * Read-only viewer for the tenant's submitted checklist (BR-33 / BR-34). No edit, capture, remove,
 * submit or correction control — the landlord only reads what the tenant recorded.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Modal } from '../Modal';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { EmptyState } from '../layout';
import { ChecklistStatusBadge } from './ChecklistStatusBadge';
import { groupItemsByRoom } from '../../lib/condition-rooms';
import {
  DEFICIENCY_TYPE_LABELS,
  conditionPhotoDownloadUrl,
  getWalkthrough,
  type ConditionItemPhoto,
  type ConditionItemResponse,
  type WalkthroughPhase,
  type WalkthroughSummaryResponse,
} from '../../lib/condition.api';

interface TenantChecklistModalProps {
  walkthroughId: string;
  phase: WalkthroughPhase;
  onClose: () => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; summary: WalkthroughSummaryResponse; items: ConditionItemResponse[] };

function PhotoThumb({ photo, itemName, index, total }: { photo: ConditionItemPhoto; itemName: string; index: number; total: number }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    const processing = photo.status === 'PENDING';
    return (
      <div
        className="flex h-16 w-16 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-border bg-muted p-1 text-center"
        data-testid={`tenant-checklist-photo-${photo.id}`}
        role={processing ? 'status' : undefined}
      >
        {processing ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
        )}
        <span className="text-[10px] leading-tight text-muted-foreground">{processing ? 'Processing…' : 'Unavailable'}</span>
      </div>
    );
  }
  return (
    <img
      src={conditionPhotoDownloadUrl(photo.id)}
      alt={`${itemName} photo ${index + 1} of ${total}`}
      className="h-16 w-16 rounded-md border border-border object-cover"
      data-testid={`tenant-checklist-photo-${photo.id}`}
      onError={() => setFailed(true)}
    />
  );
}

function PhotoThumbs({ item }: { item: ConditionItemResponse }) {
  return (
    <div className="flex flex-wrap gap-2">
      {item.photos.map((photo, i) => (
        <PhotoThumb key={photo.id} photo={photo} itemName={item.itemName} index={i} total={item.photos.length} />
      ))}
    </div>
  );
}

function FlaggedItem({ item }: { item: ConditionItemResponse }) {
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3"
      data-testid={`tenant-checklist-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">{item.itemName}</div>
          <div className="text-xs text-muted-foreground">{item.roomName}</div>
        </div>
        <Badge variant="warning" className="shrink-0">
          <AlertTriangle className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Flagged
        </Badge>
      </div>
      {item.deficiencyTypes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.deficiencyTypes.map((type) => (
            <Badge key={type} variant="secondary">
              {DEFICIENCY_TYPE_LABELS[type]}
            </Badge>
          ))}
        </div>
      )}
      {item.narrative && <p className="m-0 text-sm text-foreground">{item.narrative}</p>}
      {item.photos.length > 0 && <PhotoThumbs item={item} />}
    </div>
  );
}

function OkItem({ item }: { item: ConditionItemResponse }) {
  const [expanded, setExpanded] = useState(false);
  const count = item.photos.length;
  const panelId = `tenant-checklist-photos-${item.id}`;
  return (
    <div className="border-b border-border last:border-b-0" data-testid={`tenant-checklist-item-${item.id}`}>
      <div className="flex min-h-[44px] items-center justify-between gap-2">
        <div className="min-w-0 text-sm">
          <span className="font-medium text-foreground">{item.itemName}</span>{' '}
          <span className="text-xs text-muted-foreground">· {item.roomName}</span>
        </div>
        {count > 0 && (
          <Button
            type="button"
            variant="ghost"
            className="shrink-0 px-2"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((v) => !v)}
            data-testid={`tenant-checklist-photos-toggle-${item.id}`}
          >
            {count} {count === 1 ? 'photo' : 'photos'}
            {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          </Button>
        )}
      </div>
      {expanded && count > 0 && (
        <div id={panelId} className="pb-3">
          <PhotoThumbs item={item} />
        </div>
      )}
    </div>
  );
}

export function TenantChecklistModal({ walkthroughId, phase, onClose }: TenantChecklistModalProps) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(() => {
    setState({ status: 'loading' });
    getWalkthrough(walkthroughId)
      .then(({ summary, items }) => setState({ status: 'ready', summary, items }))
      .catch(() => setState({ status: 'error' }));
  }, [walkthroughId]);

  useEffect(() => {
    load();
  }, [load]);

  const { flagged, ok } = useMemo(() => {
    if (state.status !== 'ready') return { flagged: [], ok: [] };
    // Flagged first; room grouping keeps the runner's room/item order within each section.
    const ordered = groupItemsByRoom(state.items).flatMap((g) => g.items);
    return {
      flagged: ordered.filter((i) => i.condition === 'DEFICIENT'),
      ok: ordered.filter((i) => i.condition !== 'DEFICIENT'),
    };
  }, [state]);

  const title = `Tenant's ${phase === 'MOVE_IN' ? 'move-in' : 'move-out'} checklist`;

  return (
    <Modal isOpen onClose={onClose} title={title} data-testid="tenant-checklist-modal">
      {state.status === 'loading' && (
        <div role="status" aria-label="Loading checklist" className="flex flex-col gap-3" data-testid="tenant-checklist-loading">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      )}

      {state.status === 'error' && (
        <div role="alert" className="flex flex-col items-start gap-2" data-testid="tenant-checklist-error">
          <p className="m-0 text-sm text-foreground">Couldn&apos;t load the tenant&apos;s checklist.</p>
          <Button type="button" variant="outline" className="text-foreground" onClick={load} data-testid="tenant-checklist-retry">
            Retry
          </Button>
        </div>
      )}

      {state.status === 'ready' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <ChecklistStatusBadge state="SUBMITTED" submittedAt={state.summary.submittedAt} />
            <span className="text-xs text-muted-foreground">Read-only</span>
          </div>

          {state.items.length === 0 ? (
            <EmptyState
              title="No items were recorded"
              description="The tenant submitted this checklist without any items."
              data-testid="tenant-checklist-empty"
            />
          ) : (
            <>
              {flagged.length > 0 && (
                <section aria-label={`Needs attention · ${flagged.length}`} className="flex flex-col gap-2">
                  <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-foreground">
                    Needs attention · {flagged.length}
                  </h3>
                  {flagged.map((item) => (
                    <FlaggedItem key={item.id} item={item} />
                  ))}
                </section>
              )}
              {ok.length > 0 && (
                <section aria-label={`OK · ${ok.length}`} className="flex flex-col gap-2">
                  <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-foreground">
                    OK · {ok.length}
                  </h3>
                  <div>
                    {ok.map((item) => (
                      <OkItem key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          <div className="flex justify-end">
            <Button type="button" variant="outline" className="text-foreground" onClick={onClose} data-testid="tenant-checklist-close">
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
